import { useState } from 'react';

const DEFAULT_PATTERN = /^\d+$/;

// Manages the type-then-commit chip UX for a Formik-owned array field; unifies the old useNationalCode/useMultiNationalCode hooks.
export function useChipCodesField({
    label,
    codes = [],
    onChange,
    pattern = DEFAULT_PATTERN,
}) {
    const [input, setInput] = useState('');
    const [error, setError] = useState({
        isError: false,
        message: '',
        duplicates: '',
    });

    const clearError = () =>
        setError({ isError: false, message: '', duplicates: '' });

    const addCodes = (codesToAdd) => {
        onChange([...codes, ...codesToAdd]);
        setInput('');
    };

    const handleChange = (e) => {
        clearError();
        setInput(e.target.value.trim());
    };

    const handleKeyDown = (e) => {
        if (!['Enter', 'Tab', ','].includes(e.key)) return;
        e.preventDefault();
        clearError();
        if (!input) return;

        if (codes.includes(input)) {
            setError({
                isError: true,
                message: '',
                duplicates: `${label} already exists ${input}.`,
            });
            return;
        }
        if (!pattern.test(input)) {
            setError({
                isError: true,
                message: `Invalid ${label} ${input}.`,
                duplicates: '',
            });
            return;
        }
        addCodes([input]);
    };

    const handlePaste = (e) => {
        e.preventDefault();
        clearError();

        const pastedData = e.clipboardData.getData('text');
        const pastedCodes = pastedData
            .trim()
            .replace(/\s+/g, '')
            .split(',')
            .filter((value) => value !== '');

        const invalid = [];
        const duplicates = [];
        const allowed = [];
        const seen = new Set(codes);

        pastedCodes.forEach((code) => {
            if (seen.has(code)) {
                duplicates.push(code);
                return;
            }
            if (!pattern.test(code)) {
                invalid.push(code);
                return;
            }
            allowed.push(code);
            seen.add(code);
        });

        if (allowed.length) addCodes(allowed);

        if (duplicates.length) {
            setError((prev) => ({
                ...prev,
                isError: true,
                duplicates: `${label} already exists ${duplicates.join(', ')}.`,
            }));
        }
        if (invalid.length) {
            setError((prev) => ({
                ...prev,
                isError: true,
                message: `Invalid ${label} ${invalid.join(', ')}.`,
            }));
        }
    };

    const handleCloseClick = (_code, index) =>
        onChange(codes.filter((_c, i) => i !== index));

    return {
        input,
        error,
        handleChange,
        handleKeyDown,
        handlePaste,
        handleCloseClick,
    };
}
